import { defineRoute } from './defineRoute';

/**
 * 29CM 도메인에서 노출되는 URL = 큐레이터 앱 내부 Next.js 라우트와 1:1.
 *
 * 일부 라우트는 무신사 프록시에는 파라미터가 있지만 29CM에는 정적 경로다.
 * (예: myShop은 무신사 `/curator/myshop/:snsTitle`, 29CM `/myshop`.)
 * API 통일을 위해 같은 시그니처로 노출하고 29CM는 인자를 무시한다.
 *
 * 부정벡터:
 * - Musinsa-only deeplink는 paths29CM에 키 없음 → SharedPaths에서 자동 제외 안 됨(profileEdit 키는 양쪽 다 있어 SharedPaths 포함되지만 메커니즘 다름 → useGoProfileEdit hook 필수)
 * - 29CM-only 키(entry, profileEditNickname): pathsMusinsa에 없어 SharedPaths에서 제외 → getPaths() 접근 시 컴파일 에러
 *
 * 모든 키는 factory 함수. `paths.intro()` 형태로 호출하며 `{ query: {...} }` 옵션 지원.
 */
export const paths29CM = {
  // Group 1
  intro: defineRoute(() => '/intro'),
  join: defineRoute(() => '/join'),
  me: defineRoute(() => '/me'),
  events: defineRoute(() => '/events'),
  terms: defineRoute(() => '/terms'),
  policy: defineRoute(() => '/policy'),
  // 시딩 약관 본문 — desktop 웹에서 "자세히" 를 새 탭으로 연다(모바일/웹뷰는 바텀시트).
  // 기존 /terms·/policy 처럼 약관을 목적별 경로(slug)로 구분. (termId = kebab slug)
  seedingTerm: defineRoute(({ termId }: { termId: string }) => `/campaign/seeding/terms/${termId}`),

  // Group 2
  earningsSummary: defineRoute(() => '/earnings-summary'),
  // 정산정보 등록/변경 라우트 분리. create=미등록(getAccountInfo 미요청)·edit=등록됨(요청). (SID-4631)
  accountCreate: defineRoute(() => '/create/account'),
  accountEdit: defineRoute(() => '/edit/account'),
  salesRanking: defineRoute(() => '/me/sales-ranking'),
  search: defineRoute(() => '/search'),
  curationInfo: defineRoute(() => '/profile/curation-info'),
  curationOrder: defineRoute(() => '/myshop/curation-edit-order'),
  uploadGoods: defineRoute(() => '/shop-edit/new/goods'),
  uploadSnap: defineRoute(() => '/shop-edit/new/snap'),
  notFound: defineRoute(() => '/not-found'),
  shop: defineRoute(({ snsTitle }: { snsTitle: string }) => `/shop/${snsTitle}`),
  myShop: defineRoute((_params: { snsTitle: string }) => '/myshop'),
  editCuration: defineRoute(({ curationId }: { curationId: string }) => `/shop-edit/${curationId}`),
  snapDetail: defineRoute(({ curationId }: { curationId: string }) => `/snap-detail/${curationId}`),
  profileSns: defineRoute((_params: { shopSlug: string }) => '/profile/sns'),
  goods: defineRoute(({ hashId }: { hashId: string }) => `/short-link/goods/${hashId}`),

  // Group 3 — 캠페인·쿠폰은 무신사 전용. paths29CM에 정의 없음 → SharedPaths에서 자동 제외.
  // 29CM 환경에서는 middleware가 /campaign/*, */coupon 라우트를 not-found로 차단(isMusinsaOnlyRoute).
  // 호출부는 pathsMusinsa.* 직접 import로 통일.

  // Cross-brand이나 메커니즘 다름 (Musinsa는 native deeplink, 29CM은 web route)
  profileEdit: defineRoute(() => '/profile/edit'),

  // 인증 흐름 — 양쪽 환경 동일 path. middleware의 auth error redirect 대상.
  logout: defineRoute(() => '/auth/logout'),

  // 29CM-only (proxy 표에 대응 없음)
  entry: defineRoute(() => '/entry'),
  profileEditNickname: defineRoute(() => '/profile/edit/nickname'),

  // 큐레이터 시딩
  settings: defineRoute(() => '/me/settings'),
  campaignCollaboration: defineRoute(() => '/campaign/collaboration'),
  // 캠페인 신청 목록 (SID-3485) — 큐레이터가 신청 가능한 모집 중 시딩 캠페인 PLP
  campaignRecruitment: defineRoute(() => '/campaign/recruitment'),
  campaignSeeding: defineRoute(() => '/campaign/seeding'),
  // 시딩 캠페인 상세 — 모집형(큐레이터가 둘러보고 신청)과 제안형(브랜드가 1:1 제안)을
  // path 로 분리. dynamic segment 는 BE 가 발급하는 campaignId (모집형은 캠페인 자체의 id).
  // 모집형 상세 3-path 분리:
  //   - Detail : 신청 화면 (BEFORE_APPLY, 전품절 SOLD_OUT_UNAVAILABLE 도 신청 화면+CTA 비활성)
  //   - Application : 신청 후 조회·취소 (APPLIED / AWAITING_SELECTION / NOT_SELECTED / SELF_WITHDRAWN)
  //   - Progress : 선정 후 진행 (SELECTED_PREPARING ~ CONTENT_SUBMITTED / CONTENT_EXPIRED)
  // 진입 동선은 리스트가 status 보고 분기. 각 path 는 알맞은 status 만 받음.
  // 통일 상세(참여-후) — campaignId 만으로 진입, type×status 는 화면이 분기. 모집 신청(BEFORE_APPLY)만
  // 별도 recruitmentDetail(신청 화면) 유지.
  campaignSeedingDetail: defineRoute(
    ({ campaignId }: { campaignId: number | string }) => `/campaign/seeding/${campaignId}`,
  ),
  campaignSeedingRecruitmentDetail: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/campaign/seeding/recruitment/${campaignId}`,
  ),
  campaignSeedingRecruitmentApplication: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/campaign/seeding/recruitment/${campaignId}/application`,
  ),
  campaignSeedingRecruitmentProgress: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/campaign/seeding/recruitment/${campaignId}/progress`,
  ),
  // 제안형 — 수락 전 검토(PENDING/PRODUCT_SELECTED/SOLD_OUT)는 통일 상세(`campaignSeedingDetail`)가
  // 담당, 수락 후 진행(PREPARING~CONTENT_SUBMITTED)만 Progress 라우트.
  // (구 campaignSeedingProposalDetail 은 page 부재로 제거 — 통일 상세로 일원화)
  campaignSeedingProposalProgress: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/campaign/seeding/proposal/${campaignId}/progress`,
  ),
  campaignSeedingRecruitmentContentPeriod: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/campaign/seeding/recruitment/${campaignId}/content-period`,
  ),
  campaignSeedingProposalContentPeriod: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/campaign/seeding/proposal/${campaignId}/content-period`,
  ),
  // 콘텐츠 링크 제출 페이지 — 배송 완료 후 단계부터 진입.
  // PRD §3-1 "링크 수정 가능", §3-4 "수정 가능 / 삭제 불가" — 단일 페이지 안에서 edit ↔ view 토글.
  campaignSeedingRecruitmentContentSubmission: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/campaign/seeding/recruitment/${campaignId}/content-submission`,
  ),
  campaignSeedingProposalContentSubmission: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/campaign/seeding/proposal/${campaignId}/content-submission`,
  ),
} as const;
